/**
 * HTTP client for the Coastal Bizz Connect REST API
 *
 * Backend contract:  { success: boolean, data: T, meta?: PaginationMeta, message?: string }
 * All helpers unwrap `data` automatically so callers get typed payloads directly.
 *
 * Set VITE_PUBLIC_API_URL to connect the live backend.
 * Leave it empty → app runs in demo mode with mock data.
 */

const rawBase = import.meta.env.VITE_PUBLIC_API_URL as string | undefined;

export const API_BASE_URL = (rawBase ?? "").replace(/\/+$/, "");
const DEFAULT_TIMEOUT_MS  = 12_000;  // 12 s — avoids hanging on slow VPS cold start
const MAX_RETRIES         = 1;        // one silent retry on network error (not 4xx/5xx)

export function isApiConfigured(): boolean {
  return API_BASE_URL.length > 0;
}

// ── Token storage keys (shared with the auth service) ─────────────────────────

export const ACCESS_TOKEN_KEY  = "cbc_at";
export const REFRESH_TOKEN_KEY = "cbc_rt";

// ── Automatic access-token refresh on 401 ─────────────────────────────────────
//
// Access tokens are short-lived (minutes). When a request comes back 401 we
// silently exchange the stored refresh token for a new pair and retry once, so
// short-lived access tokens never bounce the user out mid-session.

type TokensRefreshedListener = (accessToken: string, refreshToken: string) => void;
let tokensRefreshedListener: TokensRefreshedListener | null = null;

/** Register a callback so the app can update its in-memory access token. */
export function onTokensRefreshed(fn: TokensRefreshedListener | null): void {
  tokensRefreshedListener = fn;
}

let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;
  try {
    const response = await fetchWithTimeout(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken }),
    });
    if (!response.ok) return null;
    const envelope = (await response.json()) as ApiEnvelope<{
      accessToken: string;
      refreshToken: string;
    }>;
    const data = envelope.data;
    if (!data?.accessToken) return null;
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    if (data.refreshToken) localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    tokensRefreshedListener?.(data.accessToken, data.refreshToken ?? refreshToken);
    return data.accessToken;
  } catch {
    return null;
  }
}

/** Deduplicate concurrent refreshes — one refresh, every caller awaits it. */
function triggerTokenRefresh(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = performRefresh().finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// ── Typed errors ──────────────────────────────────────────────────────────────

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly payload?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class NetworkError extends Error {
  constructor(message = "Network request failed — check your connection.") {
    super(message);
    this.name = "NetworkError";
  }
}

// ── Backend envelope ──────────────────────────────────────────────────────────

interface ApiEnvelope<T> {
  success: boolean;
  data:    T;
  message?: string;
  meta?:   PaginationMeta;
}

export interface PaginationMeta {
  page:       number;
  limit:      number;
  total:      number;
  totalPages: number;
}

export interface PagedResult<T> {
  data: T[];
  meta: PaginationMeta;
}

// ── Core fetch helper ─────────────────────────────────────────────────────────

let _requestCounter = 0;

async function fetchWithTimeout(
  url: string,
  options: Parameters<typeof fetch>[1],
  timeoutMs = DEFAULT_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const signal = options.signal
    ? AbortSignal.any([options.signal, controller.signal])
    : controller.signal;

  try {
    return await fetch(url, { ...options, signal });
  } finally {
    clearTimeout(timer);
  }
}

async function _request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  attempt = 0,
  extraHeaders?: Record<string, string>,
  authRetry = false,
): Promise<T> {
  const requestId = `cbc-${++_requestCounter}-${Date.now()}`;
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    Accept:           "application/json",
    "X-Request-ID":   requestId,
    ...extraHeaders,
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  let response: Response;
  try {
    response = await fetchWithTimeout(
      url,
      { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined, signal },
    );
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") throw err;
    // Retry once on pure network failure (ECONNRESET, offline, etc.)
    if (attempt < MAX_RETRIES) {
      await new Promise((r) => setTimeout(r, 400));
      return _request<T>(method, path, body, signal, attempt + 1, extraHeaders);
    }
    throw new NetworkError();
  }

  // 401 on an authenticated call → refresh once, then retry with the new token.
  if (response.status === 401 && extraHeaders?.Authorization && !authRetry) {
    const newToken = await triggerTokenRefresh();
    if (newToken) {
      return _request<T>(method, path, body, signal, attempt, {
        ...extraHeaders,
        Authorization: `Bearer ${newToken}`,
      }, true);
    }
  }

  if (!response.ok) {
    let payload: unknown;
    try { payload = await response.json(); } catch { /* non-JSON error body */ }
    const message =
      (payload as ApiEnvelope<never>)?.message ?? `API error ${response.status}`;
    throw new ApiError(message, response.status, payload);
  }

  const envelope = (await response.json()) as ApiEnvelope<T>;
  return envelope.data;
}

// ── Public helpers ────────────────────────────────────────────────────────────

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return _request<T>("GET", path, undefined, signal);
}

export function apiPost<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return _request<T>("POST", path, body, signal);
}

export function apiPatch<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  return _request<T>("PATCH", path, body, signal);
}

/** Authenticated GET — injects Bearer token header */
export function apiGetAuth<T>(path: string, token: string, signal?: AbortSignal): Promise<T> {
  return _request<T>("GET", path, undefined, signal, 0, { Authorization: `Bearer ${token}` });
}

/** Authenticated POST — injects Bearer token header */
export function apiPostAuth<T>(path: string, body: unknown, token: string, signal?: AbortSignal): Promise<T> {
  return _request<T>("POST", path, body, signal, 0, { Authorization: `Bearer ${token}` });
}

/** Authenticated PATCH — injects Bearer token header */
export function apiPatchAuth<T>(path: string, body: unknown, token: string, signal?: AbortSignal): Promise<T> {
  return _request<T>("PATCH", path, body, signal, 0, { Authorization: `Bearer ${token}` });
}

/** Authenticated DELETE — injects Bearer token header */
export function apiDeleteAuth<T = void>(path: string, token: string, signal?: AbortSignal): Promise<T> {
  return _request<T>("DELETE", path, undefined, signal, 0, { Authorization: `Bearer ${token}` });
}

/** GET a paginated endpoint — returns { data, meta } */
export async function apiGetPaged<T>(
  path: string,
  signal?: AbortSignal,
): Promise<PagedResult<T>> {
  const requestId = `cbc-${++_requestCounter}-${Date.now()}`;
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Request-ID": requestId,
  };

  let response: Response;
  try {
    response = await fetchWithTimeout(url, { method: "GET", headers, signal });
  } catch {
    throw new NetworkError();
  }

  if (!response.ok) {
    let payload: unknown;
    try { payload = await response.json(); } catch { /* skip */ }
    throw new ApiError(
      (payload as ApiEnvelope<never>)?.message ?? `API error ${response.status}`,
      response.status,
      payload,
    );
  }

  const envelope = (await response.json()) as { data: T[]; meta: PaginationMeta };
  return { data: envelope.data, meta: envelope.meta };
}

/** Authenticated GET a paginated endpoint — returns { data, meta } */
export async function apiGetPagedAuth<T>(
  path: string,
  token: string,
  signal?: AbortSignal,
): Promise<PagedResult<T>> {
  const requestId = `cbc-${++_requestCounter}-${Date.now()}`;
  const url = `${API_BASE_URL}${path}`;

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Request-ID": requestId,
    Authorization: `Bearer ${token}`,
  };

  let response: Response;
  try {
    response = await fetchWithTimeout(url, { method: "GET", headers, signal });
  } catch {
    throw new NetworkError();
  }

  if (!response.ok) {
    let payload: unknown;
    try { payload = await response.json(); } catch { /* skip */ }
    throw new ApiError(
      (payload as ApiEnvelope<never>)?.message ?? `API error ${response.status}`,
      response.status,
      payload,
    );
  }

  const envelope = (await response.json()) as { data: T[]; meta: PaginationMeta };
  return { data: envelope.data, meta: envelope.meta };
}